<template>
  <main v-loading="loading" class="rebate-tab">
    <div v-if="!showPage" style="height: 300px" />
    <template v-if="showPage && !registered">
      <p>{{ $t('member.tab.rebate.notRegistered') }}</p>
    </template>
    <template v-if="showPage && registered">
      <WalletStatistic :memberId="memberId" :loading="loading" :rebateWalletBalance="rebateWalletBalance" />
      <UseRebateBlock :memberId="memberId" :walletBalance="rebateWalletBalance" :isOrgEnabled="isCashbackReadOnly" @refresh="refresh" />
      <SectionTitle :title="$t('member.tab.rebate.usageRecord')" hideBtn />
      <FiltersContainer v-if="showOperationBranch" class="my-5" flat>
        <ShopFilterSelect
          :value.sync="recordFilters.operationShopId"
          :placeholder="$t('member.tab.rebate.operationBranch')"
          @change="handleRecordFilterChange"
          @clear="handleRecordFilterChange"
        />
      </FiltersContainer>
      <BaseTable v-loading="loading" :data="tableData">
        <BaseElTableColumn prop="createdAt" :label="$t('member.tab.rebate.createdAt')" align="center">
          <template slot-scope="scope">{{ dateFormat(scope.row.createdAt) }}</template>
        </BaseElTableColumn>
        <BaseElTableColumn prop="type" :label="$t('member.tab.rebate.type')" align="center">
          <template slot-scope="scope">
            {{ get(originConfig, `${scope.row.origin}.name`) }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn prop="amount" :label="$t('member.tab.rebate.amount')" align="center">
          <template slot-scope="scope">
            <p :class="{'text-danger': scope.row.amount < 0}">
              <span>{{ scope.row.amount > 0 ? '+' : '' }}</span>
              <span>{{ scope.row.amount }}</span>
            </p>
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showOperationBranch" prop="Branch" :label="$t('member.tab.rebate.operationBranch')" align="center">
          <template slot-scope="scope">
            {{ scope.row.GrantedShop?.name || scope.row.RedemptionShop?.name || "-" }}
          </template>
        </BaseElTableColumn>
        <BaseElTableColumn prop="originMessage" :label="$t('member.tab.rebate.note')" align="center">
          <template slot-scope="scope">{{ scope.row.originMessage || '-' }}</template>
        </BaseElTableColumn>
        <BaseElTableColumn v-if="showOperationBranch" prop="IssuingBranch" :label="$t('member.tab.rebate.issuingBranch')" align="center">
          <template slot-scope="scope">
            {{ scope.row.IssuingBranch?.name || "-" }}
          </template>
        </BaseElTableColumn>
      </BaseTable>
      <Pagination
        class="pagination"
        :curPage.sync="tableOptions.page"
        :pageLimit="tableOptions.pageLimit"
        :total="tableOptions.dataCount"
        @pageChange="pageChange"
      />
    </template>
  </main>
</template>

<script setup>
import { useTable } from '@/use/table'
import { FindShopRebate, FindMemberRebateWalletRecord, FindMemberRebateWalletRecordCount } from '@/api/rebate'
import WalletStatistic from './components/WalletStatistic.vue'
import { originConfig } from '@/config/rebate'
import UseRebateBlock from './components/UseRebateBlock.vue'
import ShopFilterSelect from '@/components/Select/ShopFilterSelect.vue'
import store from '@/store'
import { computed, ref, reactive, onMounted } from 'vue'
import { get } from 'lodash'
import { useOrgBenefitControl } from '@/use/useOrgBenefitControl'

const props = defineProps({
  memberId: {
    type: String,
  },
  rebateWalletBalance: {
    type: Number,
  },
})
const emit = defineEmits(['updateWallet'])
const { pageStartIndex, dateFormat, tableOptions, tableData } = useTable()
const loading = ref(false)
const showPage = ref(false)
const registered = ref(false)
const recordFilters = reactive({
  operationShopId: null,
})
// const walletBalance = ref(0)
const shopId = computed(() => store.getters.shop)
const { isCashbackReadOnly, isCashbackBenefitSharingEnabled } = useOrgBenefitControl()
const showOperationBranch = computed(() => isCashbackBenefitSharingEnabled.value)

// const findMemberRebateWallet = async () => {
//   const [res, err] = await FindMemberRebateWallet({ shopId: shopId.value, memberId: props.memberId })
//   if (err) return (window.$message.error(err))
//   walletBalance.value = res.balance
// }

const findMemberRebateWalletRecord = async () => {
  const [res, err] = await FindMemberRebateWalletRecord({
    shopId: shopId.value,
    memberId: props.memberId,
    start: pageStartIndex.value,
    limit: tableOptions.pageLimit,
    operationShopId: recordFilters.operationShopId || undefined,
  })
  if (err) return (window.$message.error(err))
  tableData.value = res.map((item) => ({
    ...item,
    IssuingBranch: item.IssuingBranch ?? item.Shop ?? null,
  }))
}

const findMemberRebateWalletRecordCount = async () => {
  const [res, err] = await FindMemberRebateWalletRecordCount({
    shopId: shopId.value,
    memberId: props.memberId,
    operationShopId: recordFilters.operationShopId || undefined,
  })
  if (err) return (window.$message.error(err))
  tableOptions.dataCount = res.count
}

const handleRecordFilterChange = async () => {
  tableOptions.page = 1
  await refresh()
}

const pageChange = async () => {
  await refresh()
}

const refresh = async () => {
  loading.value = true
  await findShopRebate()
  showPage.value = true
  await Promise.all([
    // findMemberRebateWallet(),
    findMemberRebateWalletRecord(),
    findMemberRebateWalletRecordCount(),
  ])
  emit('updateWallet')
  loading.value = false
}

const findShopRebate = async () => {
  const [res, err] = await FindShopRebate({ shopId: shopId.value })
  if (err) return (window.$message.error(err))
  if (res.id) registered.value = true
  else loading.value = false
}

onMounted(async () => {
  await refresh()
})
</script>

<style scoped lang="postcss">

</style>
